import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, UtensilsCrossed, Store, SearchX, Info, Image, LayoutGrid, Package, Ruler, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import HelpSheet from '../shared/ui/HelpSheet';
import {
  SearchBar,
  FilterChips,
  LoadingView,
  EmptyState,
  PrimaryButton,
  StatCard,
} from '../shared/ui';
import { useAuth } from '../contexts/AuthContext';
import {
  listenToMenuItems,
  deleteMenuItem,
  setMenuItemAvailability,
} from '../shared/services/userService';
import MenuItemRow from '../components/menu/MenuItemRow';
import ConfirmDialog from '../components/menu/ConfirmDialog';

// Mirrors iOS RestaurantMenuView ("Products" tab): search bar, horizontal
// category filter, list of MenuItemRow cards, '+' in the header to add,
// swipe-to-delete -> per-row trash w/ confirm, swipe-to-toggle -> per-row
// availability toggle.
export default function MenuPage() {
  const { restaurantId } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!restaurantId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = listenToMenuItems(
      restaurantId,
      (list) => {
        setItems(list);
        setLoading(false);
      },
      (e) => {
        setError(e.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [restaurantId]);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return [...set].sort();
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q)
      );
    }
    if (category !== 'All') list = list.filter((i) => i.category === category);
    return list;
  }, [items, search, category]);

  const toggleAvailability = async (item) => {
    setBusyId(item.id);
    try {
      await setMenuItemAvailability(restaurantId, item.id, !item.isAvailable);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteMenuItem(restaurantId, pendingDelete.id);
      setPendingDelete(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <ChefHeader
        title="Products"
        right={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              aria-label="Menu help"
              className="h-9 px-2 rounded-full flex items-center gap-1 text-blue-500 active:bg-blue-50"
            >
              <Info size={18} />
              <span className="text-sm font-semibold hidden sm:inline">Menu Info</span>
            </button>
            {restaurantId ? (
              <button
                type="button"
                onClick={() => navigate('/business/menu/new')}
                aria-label="Add menu item"
                className="h-9 px-2 rounded-full flex items-center gap-1 text-[#FF6B35] active:bg-orange-50"
              >
                <Plus size={18} strokeWidth={2.5} />
                <span className="text-sm font-semibold">Add Item</span>
              </button>
            ) : null}
          </div>
        }
      />

      {!restaurantId ? (
        <EmptyState
          icon={Store}
          title="No Store Found"
          subtitle="Set up your restaurant profile before adding menu items."
          action={
            <PrimaryButton
              className="mt-3 max-w-[220px]"
              onClick={() => navigate('/business/profile')}
            >
              Set Up Your Store
            </PrimaryButton>
          }
        />
      ) : (
        <>
          {/* iOS statsRow: Items / Available / Out of Stock / Categories */}
          <div className="px-4 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatCard label="Items" value={items.length} icon={UtensilsCrossed} color="text-red-600" />
            <StatCard label="Available" value={items.filter((i) => i.isAvailable).length} icon={CheckCircle2} color="text-green-600" />
            <StatCard label="Out of Stock" value={items.filter((i) => i.trackStock === true && (i.stockCount ?? 0) <= 0).length} icon={AlertCircle} color="text-orange-600" />
            <StatCard label="Categories" value={categories.length} icon={LayoutGrid} color="text-blue-600" />
          </div>

          <div className="px-4 pt-3 space-y-1">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder="Search menu items..."
            />
            <FilterChips
              options={['All', ...categories]}
              value={category}
              onChange={setCategory}
            />
          </div>

          {error && (
            <div className="mx-4 mt-2 px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-medium">
              {error}
            </div>
          )}

          {loading ? (
            <LoadingView label="Loading menu items..." />
          ) : items.length === 0 ? (
            <EmptyState
              icon={UtensilsCrossed}
              title="No Menu Items"
              subtitle="Add your first menu item to get started"
              action={
                <PrimaryButton
                  className="mt-3 max-w-[220px]"
                  onClick={() => navigate('/business/menu/new')}
                >
                  Add Item
                </PrimaryButton>
              }
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="No matches"
              subtitle="Try a different search or category."
            />
          ) : (
            <div className="px-4 pt-2 pb-4 space-y-2.5">
              {filtered.map((item) => (
                <MenuItemRow
                  key={item.id}
                  item={item}
                  busy={busyId === item.id}
                  onOpen={() => navigate(`/business/menu/${item.id}`)}
                  onToggleAvailability={() => toggleAvailability(item)}
                  onDelete={() => setPendingDelete(item)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete Menu Item"
        message={`Delete "${pendingDelete?.name}"? This can't be undone.`}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* iOS HowToGuideView for the Products tab */}
      <HelpSheet
        open={showHelp}
        onClose={() => setShowHelp(false)}
        title="Your Products"
        subtitle="Everything on this tab is what customers see in your store."
        steps={[
          { Icon: Plus, title: 'Add an item', detail: 'Tap "+" in the header. Give it a name, description, price, category, and a photo.' },
          { Icon: Image, title: 'Photos sell', detail: 'Items with clear photos get more orders. Tap an item → Edit → Select Image to update it.' },
          { Icon: LayoutGrid, title: 'Categories', detail: 'Categories become the filter chips customers use to browse. Keep names short and clear.' },
          { Icon: Package, title: 'Track stock', detail: 'Turn on "Track Stock" and set a quantity — the item shows Out of Stock at 0 and can\'t be ordered.' },
          { Icon: Ruler, title: 'Sizes for retail', detail: 'Clothing, shoes, and other sized goods: add sizes under "Sizes & Stock" (e.g. US 9, M, waist 32) with a quantity each. Customers must pick a size before ordering.' },
          { Icon: EyeOff, title: 'Pause an item', detail: 'Toggle "Available" off to hide an item temporarily instead of deleting it.' },
        ]}
      />
    </div>
  );
}
